import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { FileText } from 'lucide-react'
import { useSelector } from 'react-redux'
import { fireAnalytics } from '../../../utils/firebase'
import { getDeviceCategory, getSessionId } from '../../../utils/analyticsHelpers.ts'
import DocumentInsightPanel from '../../../components/Dashboard/DashboardHeader/ConversationalMode/DocumentInsightPanel/DocumentInsightPanel.tsx'
import DocumentPipelineLoopLoader from '../../../components/Dashboard/DashboardHeader/ConversationalMode/DocumentPipelineLoopLoader/DocumentPipelineLoopLoader.tsx'
import ProjectAutofillCard from '../../../components/Dashboard/DashboardHeader/ConversationalMode/MessageBubble/ProjectAutofillCard.tsx'
import {
  extractProjectAutofillDraftIdFromResponse,
  getProjectAutofillDraft,
  postProjectAutofillDraftFromDocument,
  waitForProjectAutofillDocumentationPipeline,
  type DocumentInsight
} from '../../../services/projectAutofillDraftService.ts'
import {
  projectAutofillDraftApiToCardPayload,
  type ProjectAutofillPayload
} from '../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import styles from './ManualAssistedProjectPrecarga.module.scss'

const isNoisePrecargaErrorMessage = (msg: string | null | undefined): boolean =>
  typeof msg === 'string' && msg.includes('ManualAssistedProjectPrecarga_error')

const isAcceptedPrecargaPdf = (file: File): boolean => {
  const t = file.type?.trim().toLowerCase() || ''
  if (t === 'application/pdf') return true
  const n = file.name?.trim().toLowerCase() || ''
  return n.endsWith('.pdf')
}

export type ManualPrecargaPhase = 'pick' | 'processing' | 'editor'

type Phase = ManualPrecargaPhase

export interface ManualAssistedProjectPrecargaProps {
  onBackToClassic: () => void
  variant?: 'standalone' | 'embedded'
  onPhaseChange?: (phase: ManualPrecargaPhase) => void
  initialFile?: File | null
  onInitialFileConsumed?: () => void
  resumeDraftId?: string | null
  onPrecargaDraftIdChange?: (draftId: string | null) => void
}

const ManualAssistedProjectPrecarga: React.FC<ManualAssistedProjectPrecargaProps> = ({
  onBackToClassic,
  variant = 'standalone',
  onPhaseChange,
  initialFile = null,
  onInitialFileConsumed,
  resumeDraftId = null,
  onPrecargaDraftIdChange
}) => {
  const edifyUserId = useSelector((state: { general?: { userData?: { id?: string | number } } }) =>
    state.general?.userData?.id != null ? String(state.general.userData.id) : ''
  )
  const [phase, setPhase] = useState<Phase>(() =>
    initialFile != null ? 'processing' : resumeDraftId ? 'processing' : 'pick'
  )
  const [submitting, setSubmitting] = useState(false)
  const [pipelinePhase, setPipelinePhase] = useState<string | null>(null)
  const [pipelinePrimaryName, setPipelinePrimaryName] = useState<string | null>(null)
  const [pipelinePrimarySizeBytes, setPipelinePrimarySizeBytes] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pipelineFailureInsight, setPipelineFailureInsight] = useState<DocumentInsight | null>(null)
  const [cardPayload, setCardPayload] = useState<ProjectAutofillPayload | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [suppressPickDropSurface, setSuppressPickDropSurface] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pipelineFailureContinueDraftIdRef = useRef<string | null>(null)
  const pipelineFailureInsightContinueRef = useRef<DocumentInsight | null>(null)

  useEffect(() => {
    onPhaseChange?.(phase)
  }, [phase, onPhaseChange])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const handleBack = useCallback(() => {
    abortRef.current?.abort()
    onBackToClassic()
  }, [onBackToClassic])

  const runPipeline = useCallback(async (selected: File) => {
    onPrecargaDraftIdChange?.(null)
    pipelineFailureContinueDraftIdRef.current = null
    pipelineFailureInsightContinueRef.current = null
    setSuppressPickDropSurface(false)
    setError(null)
    setSubmitting(true)
    setPhase('processing')
    setPipelinePhase(null)
    setPipelinePrimaryName(null)
    setPipelinePrimarySizeBytes(null)
    setPipelineFailureInsight(null)
    setCardPayload(null)

    const ac = new AbortController()
    abortRef.current = ac

    let activeDraftId: string | null = null

    try {
      const fromDoc = await postProjectAutofillDraftFromDocument(selected)
      activeDraftId =
        fromDoc.mode === 'async'
          ? fromDoc.draftId
          : extractProjectAutofillDraftIdFromResponse(fromDoc.body)

      if (!activeDraftId) {
        if (mountedRef.current) {
          setError(
            'No se recibió un borrador del servidor. Probá de nuevo o volvé al inicio y cargá los datos a mano.'
          )
          setPhase('pick')
        }
        setSubmitting(false)
        return
      }

      fireAnalytics('documentation_analysis_started', {
        edify_user_id: edifyUserId,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: 'project_documentation',
        action: 'documentation_analysis_started',
        source: 'manual_assisted_precarga',
        draft_id: activeDraftId,
        upload_mode: fromDoc.mode,
        file_name: typeof selected.name === 'string' && selected.name.trim() ? selected.name.trim() : undefined
      })

      const finalPipeline = await waitForProjectAutofillDocumentationPipeline(
        activeDraftId,
        ({ loaderPhase, status }) => {
          if (!mountedRef.current) return
          setPipelinePhase(loaderPhase)
          setPipelinePrimaryName(status.primaryFileOriginalName ?? null)
          setPipelinePrimarySizeBytes(status.primaryFileSizeBytes ?? null)
        },
        { signal: ac.signal }
      )

      const rawDraft = await getProjectAutofillDraft(activeDraftId)
      let payload = projectAutofillDraftApiToCardPayload(rawDraft, activeDraftId)
      const pollInsight = finalPipeline.documentInsight ?? null
      if (pollInsight) {
        payload = { ...payload, documentInsight: pollInsight }
      }
      if (mountedRef.current) {
        pipelineFailureContinueDraftIdRef.current = null
        onPrecargaDraftIdChange?.(activeDraftId)
        setCardPayload(payload)
        setPhase('editor')
      }
    } catch (e: unknown) {
      const named = e as { name?: string; userMessage?: string; message?: string }
      if (named.name === 'AbortError') {
        if (mountedRef.current) {
          pipelineFailureContinueDraftIdRef.current = null
          pipelineFailureInsightContinueRef.current = null
          setPhase('pick')
        }
        setSubmitting(false)
        return
      }
      if (axios.isAxiosError(e)) {
        const data = e.response?.data
        if (data && typeof data === 'object') {
          const o = data as Record<string, unknown>
          const msg = o.message ?? o.error ?? o.mensaje
          if (typeof msg === 'string' && msg.trim()) {
            if (mountedRef.current) {
              pipelineFailureContinueDraftIdRef.current = null
              pipelineFailureInsightContinueRef.current = null
              setPipelineFailureInsight(null)
              setError(isNoisePrecargaErrorMessage(msg.trim()) ? null : msg.trim())
              setPhase('pick')
            }
            setSubmitting(false)
            return
          }
        }
      }
      const errCode = (e as { code?: string }).code
      const errInsight = (e as { documentInsight?: DocumentInsight | null }).documentInsight ?? null
      const hasPipelineInsight =
        errCode === 'DOCUMENTATION_PIPELINE_FAILED' && errInsight != null
      const msg =
        typeof named.userMessage === 'string' && named.userMessage.trim()
          ? named.userMessage.trim()
          : typeof named.message === 'string' && named.message.trim()
            ? named.message.trim()
            : 'No se pudo procesar el documento. Intentá de nuevo.'
      let displayError: string | null = msg
      if (hasPipelineInsight || isNoisePrecargaErrorMessage(displayError)) {
        displayError = null
      }
      if (mountedRef.current) {
        setPipelineFailureInsight(errCode === 'DOCUMENTATION_PIPELINE_FAILED' ? errInsight : null)
        setError(displayError)
        setPhase('pick')
        if (hasPipelineInsight && activeDraftId) {
          pipelineFailureContinueDraftIdRef.current = activeDraftId
          pipelineFailureInsightContinueRef.current = errInsight
        } else {
          pipelineFailureContinueDraftIdRef.current = null
          pipelineFailureInsightContinueRef.current = null
        }
      }
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
      abortRef.current = null
    }
  }, [onPrecargaDraftIdChange])

  useEffect(() => {
    if (!initialFile) return
    onInitialFileConsumed?.()
    void runPipeline(initialFile)
  }, [initialFile, onInitialFileConsumed, runPipeline])

  useEffect(() => {
    if (!resumeDraftId || initialFile) return

    let cancelled = false

    const load = async () => {
      setSubmitting(true)
      setError(null)
      try {
        const rawDraft = await getProjectAutofillDraft(resumeDraftId)
        if (cancelled || !mountedRef.current) return
        const payload = projectAutofillDraftApiToCardPayload(rawDraft, resumeDraftId)
        if (cancelled || !mountedRef.current) return
        onPrecargaDraftIdChange?.(resumeDraftId)
        setCardPayload(payload)
        setPhase('editor')
      } catch {
        if (cancelled || !mountedRef.current) return
        onPrecargaDraftIdChange?.(null)
        setError('No se pudo recuperar el borrador. Volvé a cargar el documento.')
        setPhase('pick')
      } finally {
        if (!cancelled && mountedRef.current) {
          setSubmitting(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [resumeDraftId, initialFile, onPrecargaDraftIdChange])

  const triggerPickPdf = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const consumePickedPdf = useCallback(
    (f: File | null | undefined) => {
      if (!f) return
      if (!isAcceptedPrecargaPdf(f)) {
        setError('Solo se admite un archivo PDF.')
        return
      }
      void runPipeline(f)
    },
    [runPipeline]
  )

  const onHiddenFileChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const f = ev.target.files?.[0] ?? null
      ev.target.value = ''
      consumePickedPdf(f ?? undefined)
    },
    [consumePickedPdf]
  )

  const handlePickDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handlePickDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handlePickDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      const f = e.dataTransfer.files?.[0]
      consumePickedPdf(f)
    },
    [consumePickedPdf]
  )

  const continueDespitePipelineInsight = useCallback(async () => {
    const id = pipelineFailureContinueDraftIdRef.current
    const insightSnap = pipelineFailureInsightContinueRef.current
    setPipelineFailureInsight(null)
    setError(null)
    if (!id) {
      pipelineFailureInsightContinueRef.current = null
      setPhase('pick')
      return
    }
    setSubmitting(true)
    try {
      const rawDraft = await getProjectAutofillDraft(id)
      if (!mountedRef.current) return
      let payload = projectAutofillDraftApiToCardPayload(rawDraft, id)
      if (insightSnap && !payload.documentInsight) {
        payload = { ...payload, documentInsight: insightSnap }
      }
      pipelineFailureContinueDraftIdRef.current = null
      pipelineFailureInsightContinueRef.current = null
      onPrecargaDraftIdChange?.(id)
      setCardPayload(payload)
      setPhase('editor')
    } catch {
      if (!mountedRef.current) return
      setError('No se pudo recuperar el borrador. Probá de nuevo o volvé a cargar el documento.')
      setPhase('pick')
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }, [onPrecargaDraftIdChange])

  const resetToPick = useCallback(() => {
    abortRef.current?.abort()
    pipelineFailureContinueDraftIdRef.current = null
    pipelineFailureInsightContinueRef.current = null
    onPrecargaDraftIdChange?.(null)
    setCardPayload(null)
    setPipelineFailureInsight(null)
    setError(null)
    setSuppressPickDropSurface(true)
    setPhase('pick')
    setSubmitting(false)
    setTimeout(() => {
      fileInputRef.current?.click()
      let cleared = false
      const releaseSuppress = () => {
        if (cleared) return
        cleared = true
        window.removeEventListener('focus', releaseSuppress)
        window.clearTimeout(fallbackRelease)
        if (mountedRef.current) {
          setSuppressPickDropSurface(false)
        }
      }
      const fallbackRelease = window.setTimeout(releaseSuppress, 2500)
      window.addEventListener('focus', releaseSuppress)
    }, 0)
  }, [onPrecargaDraftIdChange])

  const rootClass = variant === 'embedded' ? styles.rootEmbedded : styles.root
  const cardEmbed =
    variant === 'embedded' ? ('createObraDesktop' as const) : ('default' as const)

  return (
    <div className={rootClass}>
      {variant === 'standalone' && (
        <div className={styles.topBar}>
          <button type="button" className={styles.backLink} onClick={handleBack}>
            Volver al inicio
          </button>
        </div>
      )}

      {phase === 'editor' && cardPayload ? (
        <div className={styles.cardWrap}>
          <ProjectAutofillCard
            payload={cardPayload}
            embedLayout={cardEmbed}
            onRequestChangeSourceDocument={resetToPick}
          />
        </div>
      ) : null}

      {phase !== 'editor' ? (
        <div className={styles.panel}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className={styles.fileInputHidden}
            disabled={submitting}
            onChange={onHiddenFileChange}
          />
          {phase === 'processing' ? (
            <div className={styles.loaderWrap}>
              <DocumentPipelineLoopLoader
                pipelinePhase={pipelinePhase}
                primaryFileOriginalName={pipelinePrimaryName}
                primaryFileSizeBytes={pipelinePrimarySizeBytes}
              />
            </div>
          ) : (
            <>
              {variant === 'standalone' ? (
                <h2 className={styles.title}>Cargar desde documento</h2>
              ) : null}
              {pipelineFailureInsight ? (
                <DocumentInsightPanel
                  insight={pipelineFailureInsight}
                  handlers={{
                    onUploadTechnicalDoc: () => {
                      triggerPickPdf()
                    },
                    onContinueAnyway: () => {
                      void continueDespitePipelineInsight()
                    }
                  }}
                />
              ) : null}
              {error && !pipelineFailureInsight ? (
                <div className={styles.error}>{error}</div>
              ) : null}
              {!pipelineFailureInsight && !suppressPickDropSurface ? (
                <div
                  className={`${styles.pickDropZone} ${isDragOver ? styles.pickDropZoneDragOver : ''}`}
                  onDragOver={handlePickDragOver}
                  onDragLeave={handlePickDragLeave}
                  onDrop={handlePickDrop}
                >
                  <FileText size={40} strokeWidth={1.5} className={styles.pickDropIcon} aria-hidden />
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={triggerPickPdf}
                    disabled={submitting}
                  >
                    Elegir PDF
                  </button>
                  <p className={styles.pickDropHint}>O arrastrá y soltá un PDF en esta zona.</p>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default ManualAssistedProjectPrecarga
